import { isDefined } from '@e-socna/utils';
import type {
  RetryStrategyFn,
  RetryContext,
  RetryDecision,
} from '@e-socna/better-fetch';

import {
  handleNetworkError,
  calculateMinDelayForRate,
  handleServerError,
} from '../../utils/strategy.util';
import { MAX_RETRY_DELAY_MS } from '../../proxy/constants/delay.constant';

import { GEOPL_RATE_LIMITS } from '../constants/rate-limits.constant';

/**
 * Create a retry strategy for geopl API services
 *
 * @param serviceType - Type of geopl service (elevation, routing, etc.)
 * @returns Retry strategy function configured for the service rate limit
 *
 * @example
 * ```typescript
 * import { geoplRetryStrategy } from '@e-socna/gis';
 *
 * // For elevation API (5 req/s)
 * const data = await betterFetch(url, {}, {
 *   retryStrategy: geoplRetryStrategy('elevation'),
 * });
 * ```
 */

export const geoplRetryStrategy =
  (
    serviceType: keyof typeof GEOPL_RATE_LIMITS = 'elevation',
  ): RetryStrategyFn =>
  ({ statusCode, attempt, retryAfterMs }: RetryContext): RetryDecision => {
    // Network errors and timeouts
    if (typeof statusCode === 'undefined') {
      return handleNetworkError(attempt);
    }

    // 429 Rate Limit: respect Retry-After or use service-specific min delay
    if (statusCode === 429) {
      if (isDefined(retryAfterMs)) {
        return {
          shouldRetry: true,
          delayMs: Math.min(retryAfterMs, MAX_RETRY_DELAY_MS),
        };
      }

      // Calculate delay based on service rate limit
      const rateLimit = GEOPL_RATE_LIMITS[serviceType];
      const minDelay = calculateMinDelayForRate(rateLimit);
      const backoffDelay = minDelay * 2 ** attempt;

      return {
        shouldRetry: true,
        delayMs: Math.min(backoffDelay, MAX_RETRY_DELAY_MS),
      };
    }

    // 5xx Server errors
    if (statusCode >= 500 && statusCode < 600) {
      return handleServerError(attempt);
    }

    // Don't retry other status codes
    return { shouldRetry: false, delayMs: 0 };
  };
